import { Inject, inject, Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, shareReplay, startWith, switchMap, from } from 'rxjs';
import { CrmMultilocationInjectionToken, MultiLocationContext, SimplifiedUser } from '@galaxy/crm/static';
import { ACCOUNT_GROUP_TOKEN } from '../../tokens';
import { AccountGroup } from '../account-group';
import {
  AssociatedUserInterface,
  ListUsersForBusinessResponse,
  ListUsersForBusinessesResponse,
  UserService,
} from '@vendasta/business-center';

export const userFilterFn = (user: SimplifiedUser, searchTerm: string): boolean => {
  const includesFullName = user.displayName?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesFirstName = user.firstName?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesLastName = user.lastName?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesUserId = user.userId?.toLowerCase().includes(searchTerm?.toLowerCase());
  return includesFullName || includesFirstName || includesLastName || includesUserId;
};

@Injectable({ providedIn: 'root' })
export class CRMUserService {
  private userService = inject(UserService);
  private multiLocationContext$: Observable<MultiLocationContext> =
    inject(CrmMultilocationInjectionToken).multiLocationContext$;

  private allTeamMembers$ = combineLatest([this.accountGroup$.pipe(startWith(null)), this.multiLocationContext$]).pipe(
    switchMap(([accountGroup, multiLocationContext]) => {
      let accountGroupIds: string[] = [];

      if (multiLocationContext?.accountGroupsIds?.length) {
        accountGroupIds = multiLocationContext.accountGroupsIds;
      } else if (accountGroup?.accountGroupId) {
        accountGroupIds = [accountGroup.accountGroupId];
      }

      const multiLocationPath = multiLocationContext?.groupId || '';

      if (accountGroupIds.length > 1) {
        if (!multiLocationPath) {
          return of({ associatedUsers: [] } as ListUsersForBusinessesResponse);
        }

        const request = {
          accountGroupIds: accountGroupIds,
          multiLocationPath: multiLocationPath,
        };

        return from(this.userService.listUsersForBusinesses(request.accountGroupIds, request.multiLocationPath)).pipe(
          catchError(() => {
            return of({ associatedUsers: [] } as ListUsersForBusinessesResponse);
          }),
        );
      } else {
        return from(this.userService.listUsersForBusiness(accountGroup.accountGroupId)).pipe(
          catchError(() => of({ associatedUsers: [] } as ListUsersForBusinessResponse)),
        );
      }
    }),
    map((res) => {
      const associatedUsers =
        (res as ListUsersForBusinessesResponse).associatedUsers ??
        (res as ListUsersForBusinessResponse).associatedUsers ??
        [];

      return associatedUsers.map(
        (user) =>
          ({
            userId: user.userId,
            firstName: user.firstName,
            lastName: user.lastName,
            displayName: this.buildDisplayName(user),
          }) satisfies SimplifiedUser,
      );
    }),
    shareReplay(1),
  );

  constructor(@Inject(ACCOUNT_GROUP_TOKEN) private readonly accountGroup$: Observable<AccountGroup>) {}

  private buildDisplayName(user: AssociatedUserInterface): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'system';
  }

  searchUsers(searchTerm?: string): Observable<SimplifiedUser[]> {
    const search = searchTerm?.trim().replace(/\s+/g, ' ').split(' ');

    return this.allTeamMembers$.pipe(
      map((users) =>
        users.filter(
          (user) =>
            !search?.length ||
            search.some(
              (term) => user.displayName.toLowerCase().includes(term.toLowerCase()) || user.userId.includes(term),
            ),
        ),
      ),
    );
  }

  getMultiUsers(userIds: string[]): Observable<SimplifiedUser[]> {
    return this.allTeamMembers$.pipe(
      map((users) => userIds.map((userId) => users.find((u) => u?.userId === userId) ?? ({} as SimplifiedUser))),
    );
  }
}
